import {
  Card,
  CardContent,
  Button,
  Box,
  Grid,
} from "@mui/material";
import ZoneGroupIcon from "./ZoneGroupIcon";
import ControlCardActionArea from "./ControlCardActionArea";
import ControlCardTitle from "./ControlCardTitle";

// Styles found in deviceControlStyles.js
// Two buttons side by side on a 150px card: equal halves, text slightly smaller
const splitButtonSx = { minWidth: 0, flex: "1 1 0", px: 0.5, fontSize: "0.75rem" };
// The button(s) look as before; ControlCardActionArea also makes the rest of the
// card clickable: the whole card for one button, left/right half for Start/Cancel
// (selecting zones) or Stop/Resume (paused).

const ControlCardSelector = ({
  zone,
  t,
  getButtonStatus,
  isButtonDisabled,
  handleButtonClick,
  getButtonBackgroundColor,
  buttonStyles,
  selectionMode,
  onEnableSelectionMode,
  onCancelSelectionMode,
  selectedCount = 0,
}) => {
  const buttonStatus = getButtonStatus(zone);
  const disabled = isButtonDisabled(zone);
  const multiCleanStartColor = "#60e266";
  const title = t("multiClean").toUpperCase();

  const handleSingleClick = () => {
    if (buttonStatus === "start") {
      onEnableSelectionMode();
      return;
    }
    handleButtonClick(zone.id, zone.name, buttonStatus);
  };

  // What a click on the card (or each half of it) does
  let cardActions;
  if (selectionMode) {
    cardActions = [
      {
        key: "start",
        disabled: selectedCount < 2,
        onClick: () => handleButtonClick(zone.id, zone.name, "start"),
      },
      { key: "cancel", onClick: onCancelSelectionMode },
    ];
  } else if (buttonStatus === "paused") {
    cardActions = [
      {
        key: "stop",
        disabled,
        onClick: () => handleButtonClick(zone.id, zone.name, "stop"),
      },
      {
        key: "resume",
        disabled,
        onClick: () => handleButtonClick(zone.id, zone.name, "resume"),
      },
    ];
  } else {
    cardActions = [
      { key: buttonStatus, disabled, onClick: handleSingleClick },
    ];
  }

  return (
    <Grid item key="selector">
      <Card
        title={title}
        sx={{
          height: 150,
          width: 150,
          display: "flex",
          flexDirection: "column",
          border: selectionMode ? "2px solid #1976d2" : "2px solid transparent",
          boxShadow: selectionMode ? 6 : 1,
        }}
      >
        <ControlCardActionArea actions={cardActions}>
          <CardContent
            sx={{
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              p: 1.5,
            }}
          >
            {/* Icon above the name, like the zone cards */}
            <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
              <ZoneGroupIcon
                groupName={zone.nameEn}
                displayName={zone.name}
                size={18}
              />
            </Box>

            <ControlCardTitle name={title} />

            {selectionMode ? (
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() =>
                    handleButtonClick(zone.id, zone.name, "start")
                  }
                  disabled={selectedCount < 2}
                  sx={{
                    ...buttonStyles.base,
                    ...splitButtonSx,
                    backgroundColor:
                      selectedCount < 2 ? "#9e9e9e" : multiCleanStartColor,
                    "&:hover": {
                      backgroundColor:
                        selectedCount < 2 ? "#9e9e9e" : multiCleanStartColor,
                      ...buttonStyles.hover,
                    },
                  }}
                >
                  Start
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={onCancelSelectionMode}
                  sx={{
                    ...buttonStyles.base,
                    ...splitButtonSx,
                    backgroundColor: getButtonBackgroundColor("stop"),
                    "&:hover": {
                      backgroundColor: getButtonBackgroundColor("stop"),
                      ...buttonStyles.hover,
                    },
                  }}
                >
                  {t("buttonStatus.cancel").toUpperCase()}
                </Button>
              </Box>
            ) : buttonStatus === "paused" ? (
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  disabled={disabled}
                  onClick={() => handleButtonClick(zone.id, zone.name, "stop")}
                  sx={{
                    ...buttonStyles.base,
                    ...splitButtonSx,
                    backgroundColor: disabled
                      ? "#9e9e9e"
                      : getButtonBackgroundColor("stop"),
                    "&:hover": {
                      backgroundColor: disabled
                        ? "#9e9e9e"
                        : getButtonBackgroundColor("stop"),
                      ...buttonStyles.hover,
                    },
                  }}
                >
                  {t("buttonStatus.stop")}
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  disabled={disabled}
                  onClick={() =>
                    handleButtonClick(zone.id, zone.name, "resume")
                  }
                  sx={{
                    ...buttonStyles.base,
                    ...splitButtonSx,
                    backgroundColor: disabled
                      ? "#9e9e9e"
                      : getButtonBackgroundColor("resume"),
                    "&:hover": {
                      backgroundColor: disabled
                        ? "#9e9e9e"
                        : getButtonBackgroundColor("resume"),
                      ...buttonStyles.hover,
                    },
                  }}
                >
                  {t("buttonStatus.resume")}
                </Button>
              </Box>
            ) : (
              <Button
                variant="outlined"
                fullWidth
                disabled={disabled}
                onClick={handleSingleClick}
                sx={{
                  ...buttonStyles.base,
                  backgroundColor: disabled
                    ? "#9e9e9e"
                    : buttonStatus === "start"
                      ? multiCleanStartColor
                      : getButtonBackgroundColor(buttonStatus),
                  "&:hover": {
                    backgroundColor: disabled
                      ? "#9e9e9e"
                      : buttonStatus === "start"
                        ? multiCleanStartColor
                        : getButtonBackgroundColor(buttonStatus),
                    ...buttonStyles.hover,
                  },
                }}
              >
                {buttonStatus === "start"
                  ? t("buttonStatus.select").toUpperCase()
                  : t(`buttonStatus.${buttonStatus}`)}
              </Button>
            )}
          </CardContent>
        </ControlCardActionArea>
      </Card>
    </Grid>
  );
};

export default ControlCardSelector;
